import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteLabel, fetchLabels } from '../../utils/labelApi';
import { LabelIcon } from './LabelIcons';
import CreateLabelModal from './CreateLabelModal';
import EditLabelModal from './EditLabelModal';
import './labels.css';

/**
 * Gmail-style Labels block for the sidebar.
 * Per-label ⋮ menu: Edit / Delete.
 */
const LabelSidebar = ({ collapsed = false, onNavItemClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [labels, setLabels] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [menuLabelId, setMenuLabelId] = useState('');
  const [busyId, setBusyId] = useState('');

  const loadLabels = useCallback(async () => {
    try {
      const response = await fetchLabels(true);
      if (response.success) {
        setLabels((response.data || []).filter((label) => label.visible !== false));
      }
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  }, []);

  useEffect(() => {
    loadLabels();
    const refresh = () => loadLabels();
    window.addEventListener('labelsUpdated', refresh);
    return () => window.removeEventListener('labelsUpdated', refresh);
  }, [loadLabels]);

  useEffect(() => {
    if (!menuLabelId) return undefined;
    const onPointerDown = (event) => {
      if (!event.target.closest('.sidebar-label-row-wrap')) {
        setMenuLabelId('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [menuLabelId]);

  if (collapsed) return null;

  const activeLabelId = location.pathname.startsWith('/labels/')
    ? location.pathname.split('/')[2]
    : '';

  const handleDelete = async (label) => {
    if (!window.confirm(`Delete label "${label.name}"?`)) return;
    setBusyId(label._id);
    setMenuLabelId('');
    try {
      await deleteLabel(label._id);
      setLabels((prev) => prev.filter((entry) => entry._id !== label._id));
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
      if (activeLabelId === String(label._id)) {
        navigate('/inbox');
      }
    } catch (error) {
      console.error('Error deleting label:', error);
    } finally {
      setBusyId('');
    }
  };

  return (
    <section className="sidebar-section sidebar-labels-section">
      <div className="sidebar-section-title">
        <span>Tags</span>
        <button
          type="button"
          className="sidebar-add-btn"
          aria-label="Create label"
          onClick={() => setShowCreate(true)}
        >
          +
        </button>
      </div>

      {labels.map((label) => (
        <div className="sidebar-label-row-wrap" key={label._id}>
          <button
            type="button"
            className={`sidebar-label-item${activeLabelId === String(label._id) ? ' active' : ''}`}
            style={{ '--label-color': label.color || '#1a73e8' }}
            onClick={() => {
              navigate(`/labels/${label._id}`);
              onNavItemClick?.();
            }}
            title={label.name}
          >
            <span className="sidebar-label-dot" />
            <LabelIcon name={label.icon || 'label'} size={16} color={label.color || '#1a73e8'} />
            <span className="sidebar-label-name">{label.name}</span>
            {label.count > 0 && <span className="sidebar-label-count">{label.count}</span>}
          </button>

          <button
            type="button"
            className="sidebar-label-more-btn"
            aria-label={`More options for ${label.name}`}
            aria-haspopup="menu"
            aria-expanded={menuLabelId === label._id}
            disabled={busyId === label._id}
            onClick={(event) => {
              event.stopPropagation();
              setMenuLabelId((current) => (current === label._id ? '' : label._id));
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>

          {menuLabelId === label._id && (
            <div className="sidebar-label-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuLabelId('');
                  setEditingLabel(label);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                role="menuitem"
                className="is-danger"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDelete(label);
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      ))}

      {/* <button
        type="button"
        className="sidebar-create-label-btn"
        onClick={() => setShowCreate(true)}
      >
        <span>+</span>
        <span>Create label</span>
      </button> */}

      <CreateLabelModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          loadLabels();
          window.dispatchEvent(new CustomEvent('labelsUpdated'));
        }}
      />

      <EditLabelModal
        isOpen={Boolean(editingLabel)}
        label={editingLabel}
        onClose={() => setEditingLabel(null)}
        onUpdated={(updated) => {
          setLabels((prev) =>
            prev
              .map((entry) => (entry._id === updated._id ? { ...entry, ...updated } : entry))
              .filter((entry) => entry.visible !== false)
          );
          window.dispatchEvent(new CustomEvent('labelsUpdated'));
        }}
      />
    </section>
  );
};

export default LabelSidebar;
